/* eslint-disable class-methods-use-this */
// Base class + shared helpers for the third-party snippet system.
//
// The site loads a handful of third-party scripts (Intercom chat, Optimizely,
// ...) that used to be injected through Google Tag Manager. GTM is unreliable
// in privacy-hardened browsers (Safari Private Browsing, content blockers), so
// those snippets are instead described here and injected directly from the
// site's own code. Each integration is a small subclass of `ThirdPartySnippet`
// that declares WHEN it may load (triggers + optional consent category) and HOW
// it loads (`load`). A single `SnippetManager` (see `snippet-manager.js`) drives
// them from the app's core lifecycle hooks, so adding a new snippet is just a
// new subclass registered in `index.js` — no changes to the core files.
import { isBrowser, debug } from '../misc';

// When a snippet is (re)evaluated for loading. A snippet lists the triggers it
// cares about; the manager calls the matching lifecycle hook for each one.
export const TRIGGERS = {
  // As soon as the manager starts on the client (page load / hydration).
  IMMEDIATE: 'immediate',
  // When Osano consent changes — used by consent-gated snippets.
  CONSENT: 'consent',
  // The first time the visitor scrolls or hovers a link (see the pre-hydration
  // detector in `gatsby-ssr.js`). This is the "wait for engagement before
  // loading" behavior Intercom relies on.
  FIRST_INTERACTION: 'first-interaction',
  // Every Gatsby route update (including the initial render).
  ROUTE_CHANGE: 'route-change'
};

// The only hostnames the production chat/analytics should run on. This mirrors
// the old GTM container, whose Intercom triggers allowed
// `(www|pro-dev|web-dev|q2-dev|web-test).splashtop.(com|cn)` but then excluded
// every `*-dev` / `web-test` host, leaving just the live `www` domains.
export const LIVE_HOST_RE = /^www\.splashtop\.(com|cn)$/i;

export const isLiveHost = (hostname) => LIVE_HOST_RE.test(hostname || '');

// Testing/QA escape hatch so the live-only snippets can be exercised on preview
// deploys and localhost. Enable with `?forceThirdParty=1` (persisted for the
// session) or by setting `window.__splashtopForceThirdParty = true`.
const FORCE_FLAG = '__splashtopForceThirdParty';
const FORCE_STORAGE_KEY = 'forceThirdParty';

export const isForced = () => {
  if (!isBrowser) return false;
  if (window[FORCE_FLAG]) return true;

  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get(FORCE_STORAGE_KEY) === '1') {
      window[FORCE_FLAG] = true;
      try {
        window.sessionStorage.setItem(FORCE_STORAGE_KEY, '1');
      } catch (storageError) {
        debug('third-party: unable to persist force flag', storageError);
      }
      return true;
    }
    if (window.sessionStorage.getItem(FORCE_STORAGE_KEY) === '1') {
      window[FORCE_FLAG] = true;
      return true;
    }
  } catch (error) {
    debug('third-party: force-flag lookup failed', error);
  }

  return false;
};

// Convenience used by live-only snippets: load on production hosts, or anywhere
// when QA has opted in with the force flag.
export const isLiveOrForced = (hostname) => isLiveHost(hostname) || isForced();

// Injects a <script> into the document head exactly once per id. Supports an
// external `src`, an inline `innerHTML`, and an `attributes` map (e.g.
// `{ defer: '' }`). Safe to call repeatedly; a matching id short-circuits.
export const injectScript = ({ id, src, innerHTML, attributes } = {}) => {
  if (!isBrowser) return null;
  if (id && document.getElementById(id)) return document.getElementById(id);

  const script = document.createElement('script');
  if (id) script.id = id;
  if (src) script.src = src;
  if (innerHTML) script.innerHTML = innerHTML;
  if (attributes) {
    Object.entries(attributes).forEach(([name, value]) => {
      script.setAttribute(name, value);
    });
  }

  (document.head || document.documentElement).appendChild(script);
  debug('third-party: injected script', id || src);
  return script;
};

// A single third-party integration. Subclasses override `shouldLoad` / `load`
// (and optionally `onRouteChange`) to describe their behavior; the manager
// handles consent, deduped one-time loading, and lifecycle dispatch.
export class ThirdPartySnippet {
  constructor({ id, consentCategory = null, triggers = [TRIGGERS.IMMEDIATE] } = {}) {
    if (!id) throw new Error('ThirdPartySnippet requires an id');
    this.id = id;
    // Osano consent category gating this snippet (e.g. 'ANALYTICS'), or null to
    // load regardless of consent (functional scripts such as the chat widget).
    this.consentCategory = consentCategory;
    this.triggers = triggers;
  }

  hasTrigger(trigger) {
    return this.triggers.includes(trigger);
  }

  // Whether the snippet should load for the given context. `ctx` carries the
  // active `trigger`, plus `hostname`, `pathname`, `search` and `hash`.
  // Override in subclasses. Defaults to true so trigger membership alone gates.
  shouldLoad() {
    return true;
  }

  // Perform the actual injection / boot. Called at most once per page load (the
  // manager guards against repeats). Override in subclasses.
  load() {}

  // Optional hook fired on every route change once the snippet has loaded — for
  // per-navigation side effects (e.g. syncing Intercom attributes). Override as
  // needed.
  onRouteChange() {}
}

export default ThirdPartySnippet;
